import zonedTimeToUtc from 'date-fns-tz/fp/zonedTimeToUtc';
import addBusinessDays from 'date-fns/addBusinessDays';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import differenceInBusinessDays from 'date-fns/differenceInBusinessDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import format from 'date-fns/format';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import { z } from 'zod';
import { AloftObject } from './AloftObject.js';
import { AloftTime } from './AloftTime.js';
const schAloftDateJSON = z.object({
    $type: z.literal('AloftDate'),
    isoString: z.string(),
});
export class AloftDate extends AloftObject {
    oneAmLocal;
    constructor(
    /**
     * 1am in the local (i.e. JavaScript VM) time zone. This representation
     * is more convenient to use with date-fns. It's named in an obtuse way
     * to steer clear of the confusing JS historical baggage of calling the
     * native timestamp type a "Date". The use of 1am instead of midnight is
     * to avoid fencepost errors, i.e. same reason due dates are given as
     * Fri 11:59pm instead of Sat 12:00am
     */
    oneAmLocal) {
        if (!(oneAmLocal instanceof Date) || Number.isNaN(oneAmLocal.getTime())) {
            throw new Error('Invalid date');
        }
        super();
        this.oneAmLocal = oneAmLocal;
    }
    static isAloftDate(x) {
        return x instanceof AloftDate;
    }
    compare(that) {
        return this.oneAmLocal.valueOf() - that.oneAmLocal.valueOf();
    }
    // NOTE: toJSON() and schema.parse() should be inverses of each other!
    toJSON() {
        return { $type: 'AloftDate', isoString: this.toIsoString() };
    }
    toRedux() {
        return this.toJSON();
    }
    static schema = z
        .union([schAloftDateJSON, z.custom((x) => x instanceof AloftDate)])
        .transform((x) => x instanceof AloftDate ? new AloftDate(x.oneAmLocal) : AloftDate.fromIsoString(x.isoString));
    static today() {
        return AloftDate.fromIsoString(formatISO(new Date(), { representation: 'date' }));
    }
    static fromIsoString(isoString) {
        if (isoString === null || isoString === undefined)
            return isoString;
        if (typeof isoString !== 'string' || !isoString.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/)) {
            throw new Error(`Invalid ISO date string: ${isoString}`);
        }
        return new AloftDate(parseISO(`${isoString}T01:00:00`));
    }
    toEndOfDay(tz = AloftTime.getCurrentTimeZone()) {
        const date = new Date(this.oneAmLocal);
        date.setHours(23, 59, 59, 999);
        return AloftTime.fromIsoString(zonedTimeToUtc(date, tz).toISOString());
    }
    toIsoString() {
        return formatISO(this.oneAmLocal, { representation: 'date' });
    }
    toString() {
        return this.toIsoString();
    }
    /** https://date-fns.org/v2.28.0/docs/format */
    format = (formatTemplate = 'yyyy-MM-dd') => format(this.oneAmLocal, formatTemplate);
    getYear() {
        return this.oneAmLocal.getFullYear();
    }
    /**
     * Note: this is different than the JS getMonth
     * Returns 1 for January, 12 for December
     */
    getMonth() {
        return this.oneAmLocal.getMonth() + 1;
    }
    addDays(days) {
        return new AloftDate(addDays(this.oneAmLocal, days));
    }
    addBusinessDays(daysToAdd) {
        return new AloftDate(addBusinessDays(this.oneAmLocal, daysToAdd));
    }
    addMonths(months) {
        return new AloftDate(addMonths(this.oneAmLocal, months));
    }
    subtractDays(days) {
        return new AloftDate(subDays(this.oneAmLocal, days));
    }
    subtractMonths(months) {
        return new AloftDate(subMonths(this.oneAmLocal, months));
    }
    differenceInBusinessDays(that) {
        return differenceInBusinessDays(this.oneAmLocal, that.oneAmLocal);
    }
    differenceInCalendarDays(that) {
        return differenceInCalendarDays(this.oneAmLocal, that.oneAmLocal);
    }
    differenceInCalendarMonths(that) {
        return differenceInCalendarMonths(this.oneAmLocal, that.oneAmLocal);
    }
    // NumericDate
    // -----------------------------------------------------------------------------
    // A numeric date is a date encoded as a number, where one unit is equal to one
    // day. This is represented as the number of days since January 1st 2000 but we
    // reserve the right to change the reference point at any time. Comparing or
    // subtracting NumericDates will be stable even if the reference point changes.
    // Regression model parameters are based on the raw NumericDate representation,
    // so be careful when interpreting them directly. To avoid problems related to
    // the reference date changing, you should always interact with a regression by
    // plugging values into it rather than inspecting the model parameters directly.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    toNumericDate = () => this.differenceInCalendarDays(numericDateRefDate);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    static fromNumericDate = (t) => numericDateRefDate.addDays(t);
}
const numericDateRefDate = AloftDate.fromIsoString('2000-01-01');
//# sourceMappingURL=AloftDate.js.map